import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminApi } from '../../api';

const getCfg = (items, key, fallback = '') => (items || []).find(i => i.key === key)?.value || fallback;
const statusColor = (status) => status === 'healthy' || status === 'ok' ? '#66bb6a' : status === 'warning' ? '#ffb74d' : '#ef5350';

const FhirSmartIntegration = () => {
  const [config, setConfig] = useState([]);
  const [health, setHealth] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [patientQuery, setPatientQuery] = useState(null);
  const [imagingQuery, setImagingQuery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fhirBaseUrl = useMemo(() => getCfg(config, 'fhir_base_url', '/fhir'), [config]);
  const fhirServiceUrl = useMemo(() => getCfg(config, 'fhir_service_url', 'http://fhir-server:8017'), [config]);
  const smartLauncherUrl = useMemo(() => getCfg(config, 'smart_launcher_url', '/smart'), [config]);
  const browserFhirBase = '/fhir';
  const browserSmartBase = '/smart';

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [cfg, monitoring, meta, patients, imaging] = await Promise.all([
        adminApi.getConfig('fhir'),
        adminApi.getMonitoringHealth(),
        axios.get('/fhir/metadata').then(r => r.data),
        axios.get('/fhir/Patient', { params: { _count: 5 } }).then(r => r.data).catch(e => ({ error: e?.response?.data?.detail || e.message })),
        axios.get('/fhir/ImagingStudy', { params: { _count: 5 } }).then(r => r.data).catch(e => ({ error: e?.response?.data?.detail || e.message })),
      ]);
      setConfig(cfg || []);
      setHealth(monitoring || null);
      setMetadata(meta || null);
      setPatientQuery(patients || null);
      setImagingQuery(imaging || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'FHIR/SMART integration check failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const checks = health?.checks || [];
  const fhirHealth = checks.find(c => c.component === 'fhir-server');
  const smartHealth = checks.find(c => c.component === 'smart-launcher');
  const resources = metadata?.rest?.[0]?.resource?.map(r => r.type).join(', ') || 'CapabilityStatement not loaded';

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>
            FHIR / SMART Integration
          </Typography>
          <Typography sx={{ color: '#555', fontSize: 12 }}>
            Interoperability endpoints for EHR integration, FHIR R4 resources, and SMART on FHIR app launch.
          </Typography>
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} sx={{ color: '#00bcd4' }}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          ['FHIR server', fhirHealth, '/fhir/metadata'],
          ['SMART launcher', smartHealth, '/smart/launch'],
        ].map(([label, check, href]) => (
          <Grid item xs={12} md={6} key={label}>
            <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ color: '#ddd', fontWeight: 700 }}>{label}</Typography>
                <Chip size="small" label={check?.status || 'unknown'} sx={{ color: statusColor(check?.status), borderColor: statusColor(check?.status), bgcolor: '#090909' }} variant="outlined" />
              </Stack>
              <Typography sx={{ color: '#777', fontSize: 12, mb: 1 }}>{check?.details?.probe || check?.detail || 'Admin monitoring probe'}</Typography>
              <Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} href={href} target="_blank" sx={{ color: '#00bcd4', borderColor: '#00bcd4' }}>
                Open {href}
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2, mb: 2 }}>
        <Typography sx={{ color: '#ddd', fontWeight: 700, mb: 1 }}>FHIR configuration</Typography>
        <Grid container spacing={1}>
          {[
            ['fhir_service_url', fhirServiceUrl],
            ['fhir_base_url', fhirBaseUrl],
            ['smart_launcher_url', smartLauncherUrl],
            ['browser_fhir_base', browserFhirBase],
            ['browser_smart_base', browserSmartBase],
          ].map(([key, value]) => (
            <Grid item xs={12} md={6} key={key}>
              <TextField fullWidth size="small" label={key} value={value} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-input': { color: '#ccc', fontFamily: 'monospace', fontSize: 12 }, '& .MuiInputLabel-root': { color: '#777' } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e' }}>
        <Table size="small">
          <TableHead><TableRow>{['Probe', 'Result'].map(h => <TableCell key={h} sx={{ color: '#777', borderBottom: '1px solid #222' }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            <TableRow><TableCell sx={{ color: '#aaa' }}>CapabilityStatement</TableCell><TableCell sx={{ color: '#ccc' }}>{metadata?.resourceType || '—'} · {metadata?.fhirVersion || 'unknown'} · {resources}</TableCell></TableRow>
            <TableRow><TableCell sx={{ color: '#aaa' }}>Patient sample</TableCell><TableCell sx={{ color: '#ccc' }}>{patientQuery?.error || `${patientQuery?.total ?? 0} resources`}</TableCell></TableRow>
            <TableRow><TableCell sx={{ color: '#aaa' }}>ImagingStudy sample</TableCell><TableCell sx={{ color: '#ccc' }}>{imagingQuery?.error || `${imagingQuery?.total ?? 0} resources`}</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FhirSmartIntegration;
